import { Button } from "@scribenote/brian-design-system";
import type { ReactNode } from "react";

import {
  actionUsageScope,
  CATEGORY_NOUN,
  eventPolicies,
  idsFor,
  judgmentText,
  labelContext,
  labelsFor,
  noPolicyText,
  producesText,
  rootCandidateText,
  scopeVersionText,
  settledRequests,
  type CatalogEntry,
  type CatalogIndex,
} from "./catalogGraph";
import { ObservedAt } from "./CatalogTable";
import { AnswerCriteria } from "./PolicyConditions";
import { duration, percent, rate, readableEvidence } from "./traceFormat";
import type {
  CatalogAction,
  CatalogCandidate,
  CatalogCondition,
  CatalogDecisionStep,
  CatalogEvent,
  CatalogField,
  CatalogPolicy,
  CatalogScope,
} from "./types";

type Inspect = (id: string) => void;

const Heading = ({ children }: { children: ReactNode }) => (
  <h4 className="text-mono-caps text-xs text-annotation">{children}</h4>
);

const Fact = ({ term, children }: { term: string; children: ReactNode }) => (
  <div className="grid gap-1 py-1.5 sm:grid-cols-[11rem_minmax(0,1fr)]">
    <dt className="text-xs text-annotation">{term}</dt>
    <dd className="min-w-0 break-words text-sm text-ink-body">{children}</dd>
  </div>
);

const Mono = ({ children }: { children: ReactNode }) => (
  <span className="font-mono text-xs">{children}</span>
);

const None = ({ children = "None" }: { children?: ReactNode }) => (
  <span className="text-annotation">{children}</span>
);

const Names = ({ names }: { names: string[] }) =>
  names.length > 0 ? <Mono>{names.join(", ")}</Mono> : <None />;

const Links = ({
  ids,
  index,
  onInspect,
}: {
  ids: string[];
  index: CatalogIndex;
  onInspect: Inspect;
}) => {
  const labels = labelsFor(ids, index);
  const shown = ids.filter((id) => labels.has(id));
  if (shown.length === 0) return <None />;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {shown.map((id) => {
        const label = labels.get(id);
        const context = label && labelContext(label);
        return (
          <li key={id}>
            <button
              type="button"
              onClick={() => onInspect(id)}
              className="rounded-paper border border-rule bg-card px-2 py-0.5 text-left text-xs text-ink hover:bg-smudge focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {label?.name}
              {context && <span className="text-annotation"> · {context}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const Fields = ({ fields }: { fields: CatalogField[] }) =>
  fields.length > 0 ? (
    <ul className="space-y-1">
      {fields.map((field) => (
        <li key={field.name}>
          <Mono>{field.name}</Mono>{" "}
          <span className="text-xs text-annotation">
            {field.type} · {field.required ? "required" : "optional"}
          </span>
          {field.description && (
            <span className="block text-xs text-ink-body">
              {field.description}
            </span>
          )}
        </li>
      ))}
    </ul>
  ) : (
    <None />
  );

const Step = ({
  step,
  index,
  onInspect,
  candidatesLabel,
}: {
  step: CatalogDecisionStep;
  index: CatalogIndex;
  onInspect: Inspect;
  candidatesLabel: string;
}) => (
  <dl>
    <Fact term="Model judgment">
      {judgmentText(step)}
      {step.judgment_timeout_s != null &&
        ` · ${duration(step.judgment_timeout_s * 1000)} timeout`}
      {step.judgment === "rules_only" && (
        <span className="text-annotation"> · never calls a model</span>
      )}
    </Fact>
    <Fact term="On escalation">
      {step.on_escalation === "escalate"
        ? "Escalate: emit an escalation event"
        : "Record no action and stop"}
    </Fact>
    <Fact term="Context loads">
      <Names names={step.loads} />
    </Fact>
    <Fact term="Questions">
      {step.questions.length > 0 ? (
        <ul className="space-y-2">
          {step.questions.map((question) => (
            <li key={question.name}>
              <Mono>{question.name}</Mono>
              <p className="mt-0.5 whitespace-pre-wrap">
                {question.instructions}
              </p>
              <AnswerCriteria
                criteria={question.criteria}
                summary="Answer meanings"
              />
            </li>
          ))}
        </ul>
      ) : (
        <None />
      )}
    </Fact>
    <Fact term={candidatesLabel}>
      <Links ids={step.candidate_ids} index={index} onInspect={onInspect} />
    </Fact>
  </dl>
);

const Condition = ({
  condition,
  step,
}: {
  condition: CatalogCondition;
  step: CatalogDecisionStep | undefined;
}) => {
  if (condition.type === "rule")
    return (
      <li className="border-b border-rule py-2 last:border-b-0">
        <p className="text-xs text-annotation">Rule</p>
        <Mono>{condition.name}</Mono>
        {condition.description && (
          <p className="mt-0.5 text-sm">{condition.description}</p>
        )}
        {condition.check_expression && (
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-annotation">
              Check source in this build
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-paper border border-rule bg-sheet p-2 font-mono text-xs text-ink-body">
              {condition.check_expression}
            </pre>
          </details>
        )}
      </li>
    );
  const question = step?.questions.find((item) => item.name === condition.name);
  return (
    <li className="border-b border-rule py-2 last:border-b-0">
      <p className="text-xs text-annotation">Model question</p>
      <Mono>
        {condition.name} = {condition.expected_choice ?? "?"}
      </Mono>
      <p className="mt-0.5 text-xs text-annotation">
        Expects {condition.expected_choice ?? "an unrecorded choice"} · minimum
        confidence{" "}
        {condition.min_confidence == null
          ? "not defined"
          : percent(condition.min_confidence)}
      </p>
      {question && (
        <>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {question.instructions}
          </p>
          <AnswerCriteria
            criteria={question.criteria}
            summary="Answer meanings"
          />
        </>
      )}
    </li>
  );
};

const Usage = ({
  children,
  scope,
  note,
}: {
  children: ReactNode;
  scope: string;
  note?: string;
}) => (
  <section className="mt-4 border-t border-rule pt-3">
    <Heading>Observed usage · {scope}</Heading>
    <dl className="mt-1">{children}</dl>
    {note && <p className="mt-1 text-xs text-annotation">{note}</p>}
  </section>
);

const EventDetail = ({
  item,
  index,
  scope,
  onInspect,
}: {
  item: CatalogEvent;
  index: CatalogIndex;
  scope: CatalogScope;
  onInspect: Inspect;
}) => (
  <>
    <dl>
      <Fact term="Schema version">{item.schema_version}</Fact>
      <Fact term="Unique by">
        <Names names={item.unique_by} />
      </Fact>
      <Fact term="Payload fields">
        <Fields fields={item.payload_fields} />
      </Fact>
      <Fact term="Attached policy">
        {item.policy_ids.length > 0 ? (
          <Links ids={item.policy_ids} index={index} onInspect={onInspect} />
        ) : (
          <None>{noPolicyText(scope)}</None>
        )}
      </Fact>
      <Fact term="Root candidates">
        {rootCandidateText(eventPolicies(item, index)) ?? <None />}
      </Fact>
      <Fact term="Emitted by">
        <Links
          ids={[
            ...item.emitted_by_policy_ids,
            ...item.emitted_by_candidate_ids,
          ]}
          index={index}
          onInspect={onInspect}
        />
      </Fact>
    </dl>
    <Usage scope="all policy versions">
      <Fact term="Times emitted">{item.usage.emitted}</Fact>
      <Fact term="Last emitted">
        <ObservedAt
          value={item.usage.last_emitted_at}
          timezone={scope.clinic_timezone}
        />
      </Fact>
    </Usage>
  </>
);

const PolicyDetail = ({
  item,
  index,
  scope,
  onInspect,
}: {
  item: CatalogPolicy;
  index: CatalogIndex;
  scope: CatalogScope;
  onInspect: Inspect;
}) => (
  <>
    <dl>
      <Fact term="Event">
        <Mono>{item.event_kind}</Mono>
      </Fact>
      <Fact term="Version">
        <Mono>{item.version}</Mono>
      </Fact>
      <Fact term="Candidates">
        {item.root_candidate_count} root · {item.nested_candidate_count} nested
      </Fact>
      <Fact term="Emits events">
        <Links
          ids={idsFor(item.emits_event_kinds, index.eventIdByKind)}
          index={index}
          onInspect={onInspect}
        />
      </Fact>
    </dl>
    <section className="mt-3">
      <Heading>Root decision</Heading>
      <Step
        step={item.step}
        index={index}
        onInspect={onInspect}
        candidatesLabel="Root candidates, in order"
      />
    </section>
    <Usage scope={`policy ${item.version}`}>
      <Fact term="Recorded decisions">{item.usage.decisions}</Fact>
      <Fact term="Last used">
        <ObservedAt
          value={item.usage.last_decided_at}
          timezone={scope.clinic_timezone}
        />
      </Fact>
    </Usage>
  </>
);

const ARGUMENTS_TEXT: Record<CatalogCandidate["arguments"], string> = {
  none: "None",
  fixed: "Fixed values",
  computed: "Computed at decision time",
};

const CandidateDetail = ({
  item,
  index,
  scope,
  onInspect,
}: {
  item: CatalogCandidate;
  index: CatalogIndex;
  scope: CatalogScope;
  onInspect: Inspect;
}) => {
  return (
    <>
      <dl>
        <Fact term="Policy">
          <Links ids={[item.policy_id]} index={index} onInspect={onInspect} />
        </Fact>
        {item.parent_candidate_id && (
          <Fact term="Parent candidate">
            <Links
              ids={[item.parent_candidate_id]}
              index={index}
              onInspect={onInspect}
            />
          </Fact>
        )}
        <Fact term="Produces">{producesText(item)}</Fact>
        {item.reason && (
          <Fact term="Recorded reason">
            <Mono>{item.reason}</Mono>
          </Fact>
        )}
        <Fact term="Fallback">
          {item.fallback ? "Yes: chosen only when no model answered" : "No"}
        </Fact>
        {item.allow_undecided && (
          <Fact term="When Jev is unsure">Run this action</Fact>
        )}
        <Fact term="Preconditions">
          <Names names={item.preconditions} />
        </Fact>
        <Fact term="Arguments">
          {ARGUMENTS_TEXT[item.arguments]}
          {item.arguments === "fixed" && item.fixed_arguments && (
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-paper border border-rule bg-sheet p-2 font-mono text-xs">
              {readableEvidence(item.fixed_arguments)}
            </pre>
          )}
        </Fact>
      </dl>
      <section className="mt-3">
        <Heading>Conditions · all must hold</Heading>
        {item.conditions.length > 0 ? (
          <ul>
            {item.conditions.map((condition, position) => (
              <Condition
                key={`${condition.type}-${condition.name}-${position}`}
                condition={condition}
                step={index.stepOf.get(item.id)}
              />
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm">
            <None>No conditions</None>
          </p>
        )}
      </section>
      <section className="mt-3">
        <Heading>Outputs</Heading>
        <dl>
          <Fact term="Action requests">
            <Links
              ids={idsFor(item.action_types, index.actionIdByType)}
              index={index}
              onInspect={onInspect}
            />
          </Fact>
          <Fact term="Emitted events">
            <Links
              ids={idsFor(item.emits_event_kinds, index.eventIdByKind)}
              index={index}
              onInspect={onInspect}
            />
          </Fact>
        </dl>
        {item.nested_decision && (
          <div className="mt-2 rounded-paper border border-rule bg-sheet p-3">
            <Heading>Nested decision</Heading>
            <Step
              step={item.nested_decision}
              index={index}
              onInspect={onInspect}
              candidatesLabel="Nested candidates, in order"
            />
          </div>
        )}
      </section>
      <Usage
        scope={`policy ${item.version}`}
        note="Considered counts recorded evaluations of this candidate. A nested candidate is recorded only when its parent was chosen. Not chosen is not a failure."
      >
        <Fact term="Considered">{item.usage.considered}</Fact>
        <Fact term="Chosen">
          {rate(item.usage.chosen, item.usage.considered)}
        </Fact>
        <Fact term="Last chosen">
          <ObservedAt
            value={item.usage.last_chosen_at}
            timezone={scope.clinic_timezone}
            observed={item.usage.considered > 0}
            unseen="Never chosen"
          />
        </Fact>
      </Usage>
    </>
  );
};

const ActionDetail = ({
  item,
  index,
  scope,
  onInspect,
}: {
  item: CatalogAction;
  index: CatalogIndex;
  scope: CatalogScope;
  onInspect: Inspect;
}) => {
  const { usage } = item;
  const settled = settledRequests(usage);
  return (
    <>
      <dl>
        <Fact term="Schema version">{item.schema_version}</Fact>
        <Fact term="Waits for release">
          {item.waits_for_release ? "Yes" : "No"}
        </Fact>
        <Fact term="Argument fields">
          <Fields fields={item.argument_fields} />
        </Fact>
        <Fact term="Referring candidates">
          <Links
            ids={item.referring_candidate_ids}
            index={index}
            onInspect={onInspect}
          />
        </Fact>
      </dl>
      <Usage
        scope={actionUsageScope(scope)}
        note="Terminal failures are exhausted or parked runs. No change and stale are settled outcomes, not failures; in-flight requests are not settled yet."
      >
        <Fact term="Requests">{usage.requests}</Fact>
        <Fact term="Applied">
          {rate(usage.applied, settled)}{" "}
          <span className="text-xs text-annotation">of settled requests</span>
        </Fact>
        <Fact term="Settled outcomes">
          {usage.applied} applied · {usage.no_op} no change · {usage.stale}{" "}
          stale · {usage.failed} terminal failures
        </Fact>
        <Fact term="In flight">{usage.in_flight}</Fact>
        <Fact term="Last requested">
          <ObservedAt
            value={usage.last_requested_at}
            timezone={scope.clinic_timezone}
          />
        </Fact>
      </Usage>
    </>
  );
};

export const CatalogInspector = ({
  entry,
  index,
  scope,
  isAnchor,
  onInspect,
  onNavigate,
}: {
  entry: CatalogEntry;
  index: CatalogIndex;
  scope: CatalogScope;
  isAnchor: boolean;
  onInspect: Inspect;
  onNavigate: () => void;
}) => {
  const label = index.labels.get(entry.item.id);
  const context = label && labelContext(label);
  const description =
    entry.category === "events" || entry.category === "actions"
      ? entry.item.description
      : null;
  return (
    <section
      aria-label="Definition inspector"
      className="rounded-paper border border-rule bg-card p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-mono-caps text-xs text-annotation">
            Inspecting · {CATEGORY_NOUN[entry.category]}
          </p>
          <h3 className="mt-1 break-words font-display text-lg text-ink">
            {label?.name}
          </h3>
          {context && (
            <p className="mt-0.5 break-words text-xs text-annotation">
              {context}
            </p>
          )}
          {entry.category === "candidates" && (
            <p className="mt-0.5 text-xs text-annotation">
              In the {entry.item.event_kind} policy
              {entry.item.parent_candidate_id
                ? " · nested candidate"
                : " · root candidate"}
            </p>
          )}
        </div>
        {isAnchor ? (
          <span className="text-xs text-annotation">Selected in the table</span>
        ) : (
          <Button size="sm" variant="outline" onClick={onNavigate}>
            Navigate to this item
          </Button>
        )}
      </div>
      {description && (
        <p className="mt-2 text-sm text-ink-body">{description}</p>
      )}
      <div className="mt-3">
        <Heading>
          Definition ·{" "}
          {entry.category === "policies" || entry.category === "candidates"
            ? `policy ${entry.item.version}`
            : scopeVersionText(scope)}
        </Heading>
        {entry.category === "events" && (
          <EventDetail
            item={entry.item}
            index={index}
            scope={scope}
            onInspect={onInspect}
          />
        )}
        {entry.category === "policies" && (
          <PolicyDetail
            item={entry.item}
            index={index}
            scope={scope}
            onInspect={onInspect}
          />
        )}
        {entry.category === "candidates" && (
          <CandidateDetail
            item={entry.item}
            index={index}
            scope={scope}
            onInspect={onInspect}
          />
        )}
        {entry.category === "actions" && (
          <ActionDetail
            item={entry.item}
            index={index}
            scope={scope}
            onInspect={onInspect}
          />
        )}
      </div>
    </section>
  );
};
